import { useCallback, useEffect, useRef } from 'react';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import type { CarouselApi } from 'types/sharedComponents';
import { isE2ETesting } from 'utils/environment';

/**
 * Returns an Autoplay plugin for Embla carousel, or undefined during E2E testing.
 * This centralizes the E2E check to ensure all carousels consistently disable autoplay during tests.
 */
export const useAutoplayPlugin = (options: AutoplayOptionsType) => {
  if (isE2ETesting()) {
    return undefined;
  }
  return Autoplay(options);
};

/**
 * Pairs with `useAutoplayPlugin` to keep autoplay stopped across embla reInits.
 *
 * embla-carousel-autoplay re-runs its `init()` on every reInit and honors
 * `playOnInit` there too, so any reInit (mobile address-bar resize, lazy-image
 * height recalcs, `watchResize`, manual `api.reInit()`, …) will resurrect
 * autoplay even after a consumer has called `.stop()`. Invoke the returned
 * callback from the user-interaction handler that opts out of rotation — it
 * stops autoplay now and re-stops it on every subsequent reInit.
 */
export const useAutoplayStickyStop = (api: CarouselApi | undefined) => {
  const stickyRef = useRef(false);

  useEffect(() => {
    if (!api) return;
    const onReInit = () => {
      if (stickyRef.current) api.plugins()?.autoplay?.stop();
    };
    api.on('reInit', onReInit);
    return () => {
      api.off('reInit', onReInit);
    };
  }, [api]);

  const stop = useCallback(() => {
    stickyRef.current = true;
    api?.plugins()?.autoplay?.stop();
  }, [api]);

  // Reports whether the user has opted out, so callers that start autoplay
  // imperatively (e.g. on scroll-into-view) can skip it once the user has
  // taken control.
  const hasStopped = useCallback(() => stickyRef.current, []);

  return { stop, hasStopped };
};
